import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tenantsAPI, customersAPI, adminAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CountrySelect } from '../components/CountrySelect';
import { AddressFields } from '../components/AddressFields';
import { ArrowLeft, Save, Loader2, AlertCircle, Building2, MapPin, User, Info } from 'lucide-react';
import { toast } from 'sonner';

const NAVY = 'hsl(var(--c4p-navy-900))';
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const TENANT_TYPES = ['standard', 'enterprise', 'partner_internal'];

function SectionCard({ icon: Icon, iconBg, iconColor, title, subtitle, children }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div className="px-5 py-3.5 flex items-center gap-3"
        style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.4)' }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
          <Icon size={14} style={{ color: iconColor }} />
        </div>
        <div>
          <p className="text-sm font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{title}</p>
          {subtitle && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{subtitle}</p>}
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

export default function TenantCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') || null;
  const presetPartnerId = searchParams.get('preset_partner') || '';

  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState([]);

  const [form, setForm] = useState({
    company_name: '',
    tenant_type: 'standard',
    partner_id: presetPartnerId,
    partner_name: '',
    country: '',
    default_currency: 'GBP',
    // Structured address
    addr_line1: '', addr_line2: '', addr_city: '', addr_region: '', addr_postcode: '',
    // Primary contact
    contact_name: '', contact_email: '', contact_phone: '',
    notes: '',
  });

  const set = (updates) => setForm(prev => ({ ...prev, ...updates }));
  // AddressFields reads form.addr_* and form.country directly
  const hasAddress = [form.addr_line1, form.addr_city, form.addr_postcode].some(v => v?.trim());
  const hasContact = form.contact_name?.trim() || form.contact_email?.trim();

  // Derive partner name from selected partner
  const selectedPartner = partners.find(p => p.id === form.partner_id);

  useEffect(() => {
    customersAPI.list({ page_size: 200, status: 'active' })
      .then(r => {
        const list = r.data?.items || [];
        setPartners(list);
        // If preset partner, pre-fill country/currency from partner defaults
        if (presetPartnerId) {
          const p = list.find(p => p.id === presetPartnerId);
          if (p) {
            setForm(f => ({
              ...f,
              partner_id: p.id,
              partner_name: p.company_name,
              country: p.country || f.country,
              default_currency: p.default_currency || f.default_currency,
            }));
          }
        }
      })
      .catch(() => {});
  }, [presetPartnerId]);

  const handlePartnerChange = (id) => {
    const p = partners.find(p => p.id === id);
    set({
      partner_id: id === 'none' ? '' : id,
      partner_name: p?.company_name || '',
      country: p?.country || form.country,
      default_currency: p?.default_currency || form.default_currency,
    });
  };

  const handleSave = async () => {
    if (!form.company_name?.trim()) { toast.error('Company / Organisation name is required'); return; }
    setSaving(true);
    try {
      const partnerName = selectedPartner?.company_name || form.partner_name || '';
      const payload = {
        company_name: form.company_name,
        tenant_type: form.tenant_type,
        partner_id: form.partner_id || null,
        partner_name: partnerName || null,
        country: form.country || null,
        default_currency: form.default_currency,
        addr_line1: form.addr_line1 || null,
        addr_line2: form.addr_line2 || null,
        addr_city: form.addr_city || null,
        addr_region: form.addr_region || null,
        addr_postcode: form.addr_postcode || null,
        // Primary contact as legacy fields (ContactsTab added later)
        contact_name: form.contact_name || null,
        email: form.contact_email || null,
        phone_mobile: form.contact_phone || null,
        notes: form.notes || null,
        // Add to contacts array if provided
        contacts: (form.contact_name || form.contact_email) ? [{
          name: form.contact_name || '',
          title: '',
          email: form.contact_email || '',
          phone: form.contact_phone || '',
          is_primary: true,
          is_active: true,
          contact_type: 'general',
          notes: '',
        }] : [],
      };
      const { data: newTenant } = await tenantsAPI.create(payload);
      toast.success(`Tenant "${newTenant.company_name}" created`);

      if (returnTo) {
        let url = `${returnTo}?preset_tenant=${newTenant.id}`;
        if (form.partner_id) url += `&preset_partner=${form.partner_id}`;
        navigate(url);
      } else {
        navigate(`/tenants/${newTenant.id}`);
      }
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }}>
      {/* Header */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-8 pt-6 pb-4 flex items-start justify-between gap-4">
          <div>
            <button className="flex items-center gap-1.5 text-xs font-medium mb-3 hover:opacity-70 transition-opacity"
              style={{ color: 'hsl(var(--muted-fg))' }}
              onClick={() => navigate(returnTo || '/tenants')}>
              <ArrowLeft size={13} /> {returnTo ? 'Back to Quote' : 'Tenants'}
            </button>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
              New Tenant
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
              {returnTo
                ? 'Complete the tenant profile below. You\'ll be returned to the quote wizard after saving.'
                : 'Create a new tenant record. You can add additional contacts after saving.'}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2 flex-shrink-0"
            style={{ background: 'hsl(265 89% 38%)', color: 'white' }} data-testid="tenant-create-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
            Create Tenant
          </Button>
        </div>
        {returnTo && (
          <div className="px-8 pb-3">
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
              style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#6d28d9' }}>
              <Info size={12} />
              After creating the tenant, you'll be returned to the quote wizard with this tenant pre-selected.
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 px-8 py-6">
        <div className="max-w-3xl mx-auto space-y-5">

          {/* Tenant Details */}
          <SectionCard icon={Building2} iconBg="#f5f3ff" iconColor="#7c3aed"
            title="Tenant Details" subtitle="The end-customer or service recipient">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-medium">
                  Company / Organisation Name <span style={{ color: 'hsl(var(--danger))' }}>*</span>
                </Label>
                <Input value={form.company_name}
                  onChange={e => set({ company_name: e.target.value })}
                  placeholder="e.g. Acme Corporation Ltd"
                  data-testid="tenant-company-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Partner <span style={{ color: 'hsl(var(--muted-fg))' }}>(optional)</span></Label>
                <Select value={form.partner_id || 'none'} onValueChange={handlePartnerChange}>
                  <SelectTrigger><SelectValue placeholder="Link to a partner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No partner linked</SelectItem>
                    {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Country</Label>
                <CountrySelect value={form.country} onChange={v => set({ country: v })} testId="tenant-create-country" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Currency</Label>
                <Select value={form.default_currency} onValueChange={v => set({ default_currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Tenant Type</Label>
                <Select value={form.tenant_type} onValueChange={v => set({ tenant_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="partner_internal">Partner Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Notes</Label>
              <Textarea value={form.notes} onChange={e => set({ notes: e.target.value })}
                className="h-16 resize-none" placeholder="Internal notes about this tenant..." />
            </div>
          </SectionCard>

          {/* Address */}
          <SectionCard icon={MapPin} iconBg="#fef9c3" iconColor="#854d0e"
            title="Registered Address"
            subtitle="Shown on quote PDFs for Tenant Direct billing — optional but recommended">
            {!hasAddress && (
              <div className="flex items-start gap-2.5 rounded-lg px-3 py-2.5"
                style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <AlertCircle size={13} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: '#92400e' }}>
                  Address is optional but <strong>strongly recommended</strong> — it appears on Tenant Direct billing PDFs. You can complete this on the tenant profile after saving.
                </p>
              </div>
            )}
            <AddressFields
              form={form}
              setForm={setForm}
            />
          </SectionCard>

          {/* Primary Contact */}
          <SectionCard icon={User} iconBg="#f0fdf4" iconColor="#16a34a"
            title="Primary Contact"
            subtitle="The main contact at this tenant — used for signing link emails">
            {!hasContact && (
              <div className="flex items-start gap-2.5 rounded-lg px-3 py-2.5"
                style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <AlertCircle size={13} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: '#92400e' }}>
                  A contact email is needed to send signing links directly to the tenant. You can add contacts on the tenant profile after saving.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Contact Name</Label>
                <Input value={form.contact_name} onChange={e => set({ contact_name: e.target.value })}
                  placeholder="e.g. John Smith" data-testid="tenant-create-contact-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Email Address</Label>
                <Input type="email" value={form.contact_email} onChange={e => set({ contact_email: e.target.value })}
                  placeholder="john@company.com" data-testid="tenant-create-contact-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Phone</Label>
                <Input value={form.contact_phone} onChange={e => set({ contact_phone: e.target.value })}
                  placeholder="+44 7700 000000" />
              </div>
            </div>
          </SectionCard>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 pb-8">
            <Button variant="outline" onClick={() => navigate(returnTo || '/tenants')}>
              <ArrowLeft size={13} className="mr-1.5" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2"
              style={{ background: 'hsl(265 89% 38%)', color: 'white' }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
              Create Tenant
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
